import { AbstractDestroyable } from '@deqo/foundation';
import { EventRouter } from '@deqo/podopolis';
import { injectable } from 'inversify';
import { Host } from '../../Host';
import { ObjectManager } from '../../concepts/objects';
import { FontStyle, UniversalSurfaceRenderer } from '../../platform/UniversalSurfaceRenderer';
import { Line } from '../../platform/geom/Line';
import { Matrix } from '../../platform/geom/Matrix';
import { Path } from '../../platform/geom/Path';
import { Point } from '../../platform/geom/Point';
import { Rect } from '../../platform/geom/Rect';
import { resolve } from '../Container';
import { Colors } from './../../ui';
import { Camera } from './Camera';
import { Physical, RelativeTo } from './entities/Physical';
import { Root } from './entities/Root';
import { OverlayManager } from './overlay/OverlayManager';
import { ParticipantManager } from './participants/ParticipantManager';

export type DebugArtifact =
    ['objects', boolean] |
    ['point', Point | [number, number], string?, number?] |
    ['rect', Rect | Point[], string?] |
    ['circle', [Point, number], string?] |
    ['polyline', [number, number][] | readonly Point[], string?, boolean?] |
    ['path', Path | string, string?, boolean?] |
    ['line', [number, number][] | Line, string?] |
    ['text', number, number, string, string?]
    ;

export const DO = {
    set: (key: string, art: DebugArtifact) => DebugOverlay.set(key, art),
    clear: (key: string) => DebugOverlay.clear(key),
};

@injectable()
export class DebugOverlay extends AbstractDestroyable {

    private static router = new EventRouter<any>();

    public static path(key: string, px: Path | string, color?: string) {
        this.set(key, ['path', px, color]);
    }

    public static polyline(key: string, px: [number, number][] | readonly Point[], color?: string, closed?: boolean) {
        this.set(key, ['polyline', px, color, closed]);
    }

    public static line(key: string, ln: Line | [number, number][], color?: string) {
        this.set(key, ['line', ln, color]);
    }

    public static point(key: string, pt: Point | [number, number], color?: string, size?: number) {
        this.set(key, ['point', Point.from(pt), color, size]);
    }

    public static circle(key: string, circ: [Point, number], color?: string) {
        this.set(key, ['circle', circ, color]);
    }

    public static rect(key: string, rect: Rect | Point[], color?: string) {
        this.set(key, ['rect', rect, color]);
    }

    public static text(key: string, pt: Point | [number, number], value: string, color?: string) {
        pt = Point.from(pt);
        this.set(key, ['text', pt.x, pt.y, value, color]);
    }

    public static set(key: string, art: DebugArtifact): void {
        this.artifacts.set(key, art);
        this.router.emit('update', {});
    }

    public static clear(key: string): void {
        this.artifacts.delete(key);
        this.router.emit('update', {});
    }

    public static reset(): void {
        this.artifacts.clear();
        this.router.emit('update', {});
    }

    public static use(matrix: Matrix) {
        this.overrideMatrix = matrix;
    }

    public static toggleObjects() {
        this.debugObjects = !this.debugObjects;
    }

    private static artifacts = new Map<string, DebugArtifact>();
    private static debugObjects = false;
    private static overrideMatrix = null as Matrix;

    @resolve(Camera) public camera!: Camera;
    @resolve(ObjectManager) public objects!: ObjectManager;
    @resolve(OverlayManager) public overlays!: OverlayManager;
    @resolve(ParticipantManager) public participants!: ParticipantManager;

    private get paintMatrix() {
        const m = this.camera.modelViewMatrix;
        if (DebugOverlay.overrideMatrix) {
            return m.multiply(DebugOverlay.overrideMatrix);
        }
        return m;
    }

    public init() {

        if (Host.env === 'local') {
            const handle = this.overlays.createOverlay('debug', this.doPaint);
            this.overlays.add(handle);

            this.burden(() => handle);
            this.burden(() => DebugOverlay.reset());
            this.burden(this.camera.observe(() => handle.invalidate()));
            this.burden(DebugOverlay.router.on('*', () => handle.invalidate()));
            this.burden(this.objects.on('updated', () => handle.invalidate()));
        }
    }

    private doPaint = (gfx: UniversalSurfaceRenderer) => {
        const { artifacts, debugObjects } = DebugOverlay;

        for (const [type, ...data] of artifacts.values()) {
            if (type === 'point') {
                this.doPaintPoint(gfx, data);
            }
            if (type === 'rect') {
                this.doPaintRect(gfx, data);
            }
            if (type === 'circle') {
                this.doPaintCircle(gfx, data);
            }
            if (type === 'polyline') {
                this.doPaintPolyline(gfx, data);
            }
            if (type === 'path') {
                this.doPaintPath(gfx, data);
            }
            if (type === 'line') {
                this.doPaintLine(gfx, data);
            }
            if (type === 'text') {
                this.doPaintText(gfx, data);
            }
        }

        if (debugObjects) {
            for (const p of this.objects.getAll(Physical)) {
                this.doPaintDebugInfo(gfx, p);
            }
        }
    };

    private doPaintPoint(gfx: UniversalSurfaceRenderer, [pt, c, s]: any): void {

        pt = this.paintMatrix.apply(Point.from(pt));

        gfx.fillStyle = c || Colors.primary;
        gfx.beginPath();
        gfx.ellipse(pt.x, pt.y, s ?? 4, s ?? 4, 0, 0, Math.PI * 2);
        gfx.fill();
    }

    private doPaintRect(gfx: UniversalSurfaceRenderer, [r, c]: any[]): void {

        const rect: Rect = Array.isArray(r) ? Rect.fromPoints(r) : r;
        this.doPaintPolyline(gfx, [rect.points(), c, true]);
    }

    private doPaintCircle(gfx: UniversalSurfaceRenderer, [i, c]: any[]): void {

        // eslint-disable-next-line prefer-const
        let [pt, r] = i;

        pt = this.paintMatrix.apply(Point.from(pt));

        gfx.fillStyle = c || Colors.primary;
        gfx.beginPath();
        gfx.fillCircle(pt.x, pt.y, r);
        gfx.fill();
    }

    private doPaintPolyline(gfx: UniversalSurfaceRenderer, [px, stroke, closed]: any[]): void {

        const points = px.map((x: any) => this.paintMatrix.apply(Point.from(x)));
        if (!points.length) return;

        gfx.strokeStyle = stroke || Colors.primary;
        gfx.lineWidth = 2;
        gfx.beginPath();
        gfx.moveTo(points[0].x, points[0].y);
        for (const { x, y } of points) {
            gfx.lineTo(x, y);
        }
        if (closed) {
            gfx.closePath();
        }
        gfx.stroke();
    }

    private doPaintPath(gfx: UniversalSurfaceRenderer, [px, s]: any[]): void {

        if (px instanceof Path) {
            px = px.toString();
        }

        const p2d = new Path2D(px);
        gfx.strokeStyle = s || Colors.primary;
        gfx.lineWidth = 2;
        gfx.setTransform(this.paintMatrix);
        gfx.stroke(p2d);
        gfx.setTransform(Matrix.identity);
    }

    private doPaintLine(gfx: UniversalSurfaceRenderer, [ln, s]: any[]): void {

        ln = Array.isArray(ln) ? new Line(Point.fromBuffer(ln, 0), Point.fromBuffer(ln, 2)) : ln;
        this.doPaintPolyline(gfx, [[ln.p1, ln.p2], s, false]);
    }

    private doPaintText(gfx: UniversalSurfaceRenderer, [x, y, t, c]: any[]): void {

        let pt = this.paintMatrix.apply(new Point(x, y));

        gfx.fillStyle = c || Colors.primary;
        gfx.fontStyle = FontStyle.create({ family: 'Oxygen', size: 14 });

        const lines = (t || '').split(/\r?\n/);
        for (const ln of lines) {
            gfx.fillText(ln, pt.x, pt.y);
            pt = pt.add([0, 20]);
        }
    }

    private doPaintDebugInfo(gfx: UniversalSurfaceRenderer, p: Physical): void {

        if (!p.alive) return;
        if (!p.mounted()) return;
        if (p.is(Root)) return;

        gfx.fontStyle = FontStyle.create({ family: 'Courier New', size: 14 });
        gfx.fillStyle = 'whitesmoke';
        gfx.strokeStyle = 'gainsboro';

        const lines = [
            `O: ${p.id} (${p.type}), P: ${p.parent.id} [${p.order}] (${p.parent.type})`,
            `P: ${p.position(RelativeTo.Model).toString()}`,
        ];

        const width = Math.max(...lines.map(x => gfx.measureText(x).width));
        const height = (20 * lines.length) + 10;

        const pt = this.paintMatrix.apply(p.position(RelativeTo.Model));
        gfx.translate(pt.x, pt.y - height);

        gfx.fillRect(0, 0, width + 15, height);
        gfx.strokeRect(0, 0, width + 15, height);
        gfx.fillStyle = 'black';
        for (let i = 0; i < lines.length; i++) {
            gfx.fillText(lines[i], 5, 20 + (20 * i));
        }

        gfx.setTransform(Matrix.identity);
    }
}
